/**
 * ============================================================================
 * OBJECTS STORE - CREATE OPERATIONS
 * ============================================================================
 * 
 * Handles creating new objects via the backend API.
 * 
 * Data Flow:
 * 1. Process translatable fields and create string records
 * 2. Send processed data to API
 * 3. Add new object to local store
 * 4. Return created object
 * 
 * @module objects/api/create
 */

import { apiService } from "@/lib/api-functions/api-service";
import { processObjectForTranslation } from "@/lib/utils/objectParser";
import { createString } from "../../stringsStore";
import { objectsStore } from "../store";
import type { CreateObjectData, ObjectItem, ToastFunction } from "../types";

// ============================================================================
// CREATE OPERATIONS
// ============================================================================

/**
 * Create a new object via API and sync the store.
 * 
 * Process:
 * 1. Processes translatable fields to create string records
 * 2. Sends the processed object to the API
 * 3. Adds the created object to the local store
 * 
 * @param item - The object data to create
 * @param toast - Optional toast function for error reporting
 * @returns Promise resolving to the created object
 * 
 * @example
 * ```ts
 * const newObject = await createObject({
 *   game_id: 'game-123',
 *   name: 'New Character',
 *   type: 'glossary',
 *   data: [...sections],
 *   tags: ['character', 'npc'],
 * });
 * ```
 */
export async function createObject(
  item: CreateObjectData,
  toast?: ToastFunction
): Promise<ObjectItem> {
  try {
    // Process translatable fields and create strings
    const { glossaryItem: processedItem } = await processObjectForTranslation(
      item,
      createString
    );

    // Validate authentication
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Create via API using the processed item
    const result = await apiService.createObject(processedItem);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to create object');
    }

    const newItem = result.data;

    // Add the new item to the store (immutably)
    const currentData = objectsStore.getData();
    const updatedData = [...currentData, newItem];
    objectsStore.setData(updatedData);

    console.log(`[createObject] Created object: ${newItem.id} - ${newItem.name}`);
    return newItem;
    
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to create object';
    console.error('[createObject] Error:', err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
